import { useEffect, useRef } from 'react';

const IDLE_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'] as const;

interface UseIdleTimerOptions {
  /** Inactivity duration in milliseconds before onIdle fires. */
  timeout: number;
  onIdle: () => void;
}

/**
 * Calls onIdle after the user has been inactive for `timeout` ms.
 * Resets the timer on any mouse, keyboard, touch, or scroll activity.
 */
export function useIdleTimer({ timeout, onIdle }: UseIdleTimerOptions): void {
  // Keep onIdle stable so the effect doesn't re-run when it changes.
  const onIdleRef = useRef(onIdle);
  useEffect(() => {
    onIdleRef.current = onIdle;
  }, [onIdle]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    function reset() {
      clearTimeout(timer);
      timer = setTimeout(() => onIdleRef.current(), timeout);
    }

    reset();
    IDLE_EVENTS.forEach((e) => window.addEventListener(e, reset, { passive: true }));

    return () => {
      clearTimeout(timer);
      IDLE_EVENTS.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [timeout]);
}
